import React, { useCallback, useState } from "react";
import { Button, CircularProgress, TextField, styled } from "@mui/material";
import { confirmSignUp, resendConfirmationCodeFn } from "../cognito";
import CheckIcon from "@mui/icons-material/Check";

interface VerificationCodeProps {
  onVerificationComplete: (email?: string) => void;
  onError: (errMsg: string) => void;
  email: string;
}

interface VerificationCodeInputProps {
  onVerify?: (code: string) => Promise<void>;
  onChange?: (code: string) => void;
  email: string;
  onError: (errMsg: string) => void;
  verifying: boolean;
}

const VerifyCodeView = styled("div")({
  fontSize: "0.88em",
  "& .resend-code-div": {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  "& .resend-code-input-div": {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  "& .code-sent-msg": {
    color: "green",
    display: "flex",
    alignItems: "center",
    gap: 5,
    cursor: "pointer",
    "& svg": {
      width: "0.8em",
    },
  },
});

const VerificationCode = (props: VerificationCodeProps) => {
  const [verifying, setVerifying] = useState(false);
  const { email, onVerificationComplete, onError } = props;
  const handleVerifyActivationCode = useCallback(
    async (verifyCode: string) => {
      setVerifying(true);
      try {
        await confirmSignUp(email, verifyCode);
        onVerificationComplete(email);
      } catch (e) {
        onError((e as Error).message || (e as string));
      } finally {
        setVerifying(false);
      }
    },
    [email, onError, onVerificationComplete],
  );

  return (
    <form>
      <VerificationCodeInput
        email={email}
        onError={onError}
        verifying={verifying}
        onVerify={handleVerifyActivationCode}
      />
    </form>
  );
};

export const VerificationCodeInput = ({
  email,
  onError,
  verifying,
  onVerify,
  onChange,
}: VerificationCodeInputProps) => {
  //VerificationCodeInput can be used outside VerificationCode component without "onVerify" prop
  //onVerify is used in VerificationCode component to handle verification code
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [codeResent, setCodeResent] = useState(false);

  const handleVerify = () => {
    setCodeResent(false);
    if (onVerify) onVerify(code);
  };

  const handleSendCode = async () => {
    setCodeResent(false);
    setSendingCode(true);
    try {
      await resendConfirmationCodeFn(email);
      setCodeResent(true);
    } catch (e) {
      onError(e);
    } finally {
      setSendingCode(false);
    }
  };

  return (
    <VerifyCodeView className={"verify-code-view"}>
      <div className={"resend-code-div"}>
        Enter the verification code you received in your email.
        <Button
          className={"resend-code-button"}
          color={"primary"}
          onClick={handleSendCode}
          disabled={sendingCode || verifying}
          endIcon={sendingCode ? <CircularProgress size={20} /> : null}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Resend Code
        </Button>
      </div>
      (If you don't see the email, check the spam folder for mail from
      info@kahun.com)
      <div className={"resend-code-input-div"}>
        <TextField
          fullWidth
          required
          margin="normal"
          variant="outlined"
          className={"verification-code-input"}
          label="Verification Code"
          value={code}
          onChange={(e) => {
            if (onChange) onChange(e.target.value);
            setCode(e.target.value);
          }}
          disabled={verifying}
          sx={{ flexGrow: 1, flexShrink: 1, flexBasis: 300 }}
        />
        {codeResent && (
          <span
            className={"code-sent-msg"}
            onClick={() => setCodeResent(false)}
          >
            Code sent <CheckIcon />
          </span>
        )}
      </div>
      {onVerify && (
        <Button
          variant="contained"
          fullWidth
          color={"primary"}
          onClick={handleVerify}
          disabled={verifying}
          endIcon={verifying ? <CircularProgress size={20} /> : null}
        >
          Verify
        </Button>
      )}
    </VerifyCodeView>
  );
};

export default VerificationCode;
